import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Input } from '../../../../shared';
import { TEXT, PLACEHOLDER } from '../../../../../constants';
import { dateFormatChanger, showDate } from '../../../../../utils';

/**
 * CuttingFieldsGroup - Reusable component for cutting-related form fields
 * Handles both create mode (editable) and readonly mode (display only)
 * Memoized to prevent unnecessary re-renders when props haven't changed
 *
 * @param {string} mode - 'create' | 'readonly'
 * @param {Object} values - { batchQty, batchDmgQty, cutDate } (for create mode)
 * @param {Function} onCutQtyChange - Handler for cut qty change
 * @param {Function} onCutDmgQtyChange - Handler for cut damage qty change
 * @param {Function} onCutDateChange - Handler for cut date change
 * @param {Object} refs - { batchQuantity, batchDmgQuantity }
 * @param {Object} lot - Lot data (for calculating remaining qty)
 * @param {Function} getBatchQty - Function to calculate batch qty
 * @param {Object} batchData - Existing batch data (for readonly mode)
 */
const CuttingFieldsGroupComponent = ({
  mode = 'create',
  values,
  onCutQtyChange,
  onCutDmgQtyChange,
  onCutDateChange,
  refs,
  lot,
  getBatchQty,
  batchData
}) => {
  const isReadonlyMode = mode === 'readonly';

  // In readonly mode, just display existing values (used in EditBatch)
  if (isReadonlyMode) {
    return (
      <>
        <div className="col-4">{TEXT.CUT_QTY}</div>
        <div className="col-8">{batchData?.cut_qty || 0}</div>
        <div className="col-4">Damaged Cut Quantity</div>
        <div className="col-8">{batchData?.cut_damage_qty || 0}</div>
        <div className="col-4">{TEXT.CUT_DATE}</div>
        <div className="col-8">{dateFormatChanger(batchData?.cut_date)}</div>
      </>
    );
  }

  /**
   * Calculate remaining quantity available for cutting
   * Business Rules:
   * - If dyeing=false: Use remaining_qty (lot.remaining_qty already accounts for cuts)
   * - If dyeing=true: Calculate manually (total - sewed - damaged)
   *
   * This reflects different workflow paths:
   * - Non-dyed fabric: Sequential cutting workflow uses remaining_qty
   * - Dyed fabric: Parallel workflow requires manual calculation
   */
  const remainingQty = lot?.dyeing === false
    ? lot?.remaining_qty
    : lot?.total_qty - (lot?.total_sew_qty + lot?.total_damage_qty);

  return (
    <>
      {/* Cut Quantity */}
      <div className="col-4">{TEXT.CUT_QTY}</div>
      <Input
        ref={refs?.batchQuantity}
        type="number"
        className="col-8"
        placeholder={`${PLACEHOLDER.REMAINING_QTY} : ${remainingQty}`}
        value={values.batchQty}
        max={lot.total_qty - getBatchQty(lot)}
        min={0}
        onChange={(e) => {
          // Let the hook handle validation to avoid double validation
          const inputValue = e.target.value === '' ? '' : +e.target.value;
          onCutQtyChange(inputValue);
        }}
        onWheel={(e) => e.target.blur()}
      />

      {/* Damaged Cut Quantity */}
      <div className="col-4">Damaged Cut Quantity</div>
      <Input
        ref={refs?.batchDmgQuantity}
        type="number"
        className="col-8"
        value={values.batchDmgQty}
        onChange={(e) => {
          // Let the hook handle validation to avoid double validation
          const inputValue = e.target.value === '' ? '' : +e.target.value;
          onCutDmgQtyChange(inputValue);
        }}
        onWheel={(e) => e.target.blur()}
      />

      {/* Cut Date */}
      <div className="col-4">{TEXT.CUT_DATE}</div>
      <Input
        type="date"
        className="col-8"
        placeholder={PLACEHOLDER.ENTER_DATE}
        value={showDate(values.cutDate)}
        onChange={(e) => onCutDateChange(e.target.value)}
        max={showDate()}
      />
    </>
  );
};

CuttingFieldsGroupComponent.propTypes = {
  mode: PropTypes.oneOf(['create', 'readonly']),
  values: PropTypes.shape({
    batchQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    batchDmgQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    // Backend sometimes returns false/null for dates instead of strings
    cutDate: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
  }),
  onCutQtyChange: PropTypes.func,
  onCutDmgQtyChange: PropTypes.func,
  onCutDateChange: PropTypes.func,
  refs: PropTypes.shape({
    batchQuantity: PropTypes.object,
    batchDmgQuantity: PropTypes.object
  }),
  lot: PropTypes.object,
  getBatchQty: PropTypes.func,
  batchData: PropTypes.object
};

export const CuttingFieldsGroup = memo(CuttingFieldsGroupComponent);
